import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconButton, Loader, Typography, Dialog } from '@sm-dashboard/ui-components';
import { useNavigate } from 'react-router-dom';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MessageIcon from '@mui/icons-material/Message';
import PeopleIcon from '@mui/icons-material/People';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getJewishDay } from 'jewish-dates-core';
import dayjs, { type Dayjs } from 'dayjs';
import { useFetch } from '@/hooks/useFetch';
import { donationsUrl, purchasesUrl, leadsUrl } from '@/constants';
import { getUser } from '@/util/getUser';
import { useAppContext } from '@/context/context';
import { getContactDisplayName } from '@/util/contactUtils';
import { Purchase, Donation, Lead } from '@sm-dashboard/types';
import { ActivitiesFilters } from '@/components/dashboard/crm/CRM-Dashboard/ActivitiesFilters';

const CRM_CONTACTS_URL = 'pvt/crm/contacts';

export interface ActivityItem {
  id: number | string;
  type: 'donation' | 'purchase' | 'lead' | 'contact';
  createdAt: string;
  name: string;
  amount?: string;
  products?: string;
  comment?: string;
  phone?: string;
}

interface AllActivitiesDialogProps {
  open: boolean;
  onClose: () => void;
  activities?: ActivityItem[];
}

const ITEMS_PER_PAGE = 50;

const AllActivitiesDialog: React.FC<AllActivitiesDialogProps> = ({ open, onClose, activities: externalActivities }) => {
  const navigate = useNavigate();
  const user = getUser();
  const { userPermission } = useAppContext();
  const theme = useTheme();
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const [filters, setFilters] = useState<Record<string, string | string[] | number[] | null>>({});

  const { data: donationsData, loading: loadingDonations, fetchData: fetchDonations } = useFetch();
  const { data: purchasesData, loading: loadingPurchases, fetchData: fetchPurchases } = useFetch();
  const { data: leadsData, loading: loadingLeads, fetchData: fetchLeads } = useFetch();
  const { data: contactsData, loading: loadingContacts, fetchData: fetchContacts } = useFetch();
  const fetchingActivities = loadingDonations || loadingPurchases || loadingLeads || loadingContacts;

  const shouldFetchData = !externalActivities;

  const fetchedActivities = useMemo(() => {
    if (!shouldFetchData) return [];

    const activityTypes = (Array.isArray(filters.activityTypes) ? filters.activityTypes : []) as string[];
    const allActivities: ActivityItem[] = [];

    if (activityTypes.length === 0 || activityTypes.includes('donation')) {
      (donationsData?.rows || []).forEach((donation: Donation) => {
        allActivities.push({
          id: donation.id,
          type: 'donation',
          createdAt: donation.createdAt,
          name: donation.name || 'ללא שם',
          amount: donation.amount,
          comment: donation.comment,
          phone: donation.phone
        });
      });
    }

    if (activityTypes.length === 0 || activityTypes.includes('purchase')) {
      (purchasesData?.rows || []).forEach((purchase: Purchase) => {
        allActivities.push({
          id: purchase.id,
          type: 'purchase',
          createdAt: purchase.createdAt,
          name: purchase.name || 'ללא שם',
          amount: purchase.amount,
          products: purchase.products,
          comment: purchase.comment,
          phone: purchase.phone
        });
      });
    }

    if (activityTypes.length === 0 || activityTypes.includes('lead')) {
      (leadsData?.rows || []).forEach((lead: Lead) => {
        allActivities.push({
          id: lead.id,
          type: 'lead',
          createdAt: lead.createdAt,
          name: lead.name || 'ללא שם',
          comment: lead.comment,
          phone: lead.phone
        });
      });
    }

    if (activityTypes.length === 0 || activityTypes.includes('contact')) {
      (contactsData?.rows || []).forEach(
        (contact: {
          id?: number;
          fullName?: string;
          firstName?: string;
          lastName?: string;
          name?: string;
          createdAt: string;
          comment?: string;
          phone?: string;
        }) => {
          const contactName = (() => {
            const fromFirstLast = getContactDisplayName(contact);
            return fromFirstLast !== 'ללא שם' ? fromFirstLast : contact.name || 'ללא שם';
          })();

          allActivities.push({
            id: contact.id || '',
            type: 'contact',
            createdAt: contact.createdAt,
            name: contactName,
            comment: contact.comment,
            phone: contact.phone
          });
        }
      );
    }

    return allActivities.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [
    shouldFetchData,
    filters.activityTypes,
    donationsData?.rows,
    purchasesData?.rows,
    leadsData?.rows,
    contactsData?.rows
  ]);

  const activities = externalActivities || fetchedActivities;

  useEffect(() => {
    if (!open || !shouldFetchData || !user?.id) return;

    const queryParams = new URLSearchParams({
      page: '0',
      rowsPerPage: '1000'
    });

    if (userPermission?.operations?.length) {
      queryParams.append('operationsAllowed', userPermission.operations.join(','));
      queryParams.append('shaliachIdAllow', String(user.id));
    }
    queryParams.append('shaliachId', String(user.id));

    if (filters.searchTerm && typeof filters.searchTerm === 'string') {
      queryParams.append('searchTerm', filters.searchTerm);
    }
    if (filters.startDate && typeof filters.startDate === 'string') {
      queryParams.append('startDate', filters.startDate);
    }
    if (filters.endDate && typeof filters.endDate === 'string') {
      queryParams.append('endDate', filters.endDate);
    }

    const activityTypes = (Array.isArray(filters.activityTypes) ? filters.activityTypes : []) as string[];
    const shouldFetchDonations = activityTypes.length === 0 || activityTypes.includes('donation');
    const shouldFetchPurchases = activityTypes.length === 0 || activityTypes.includes('purchase');
    const shouldFetchLeads = activityTypes.length === 0 || activityTypes.includes('lead');
    const shouldFetchContacts = activityTypes.length === 0 || activityTypes.includes('contact');

    if (shouldFetchDonations) {
      fetchDonations({
        method: 'GET',
        url: `${donationsUrl}?${queryParams.toString()}`
      });
    }

    if (shouldFetchPurchases) {
      fetchPurchases({
        method: 'GET',
        url: `${purchasesUrl}?${queryParams.toString()}`
      });
    }

    if (shouldFetchLeads) {
      fetchLeads({
        method: 'GET',
        url: `${leadsUrl}?${queryParams.toString()}`
      });
    }

    if (shouldFetchContacts) {
      const contactsQueryParams = new URLSearchParams(queryParams);
      fetchContacts({
        method: 'GET',
        url: `${CRM_CONTACTS_URL}?${contactsQueryParams.toString()}`
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shouldFetchData, user?.id, filters, userPermission?.operations]);

  useEffect(() => {
    if (open) {
      setDisplayedCount(ITEMS_PER_PAGE);
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [open, activities.length]);

  const displayedActivities = activities.slice(0, displayedCount);
  const hasMore = displayedCount < activities.length;

  const loadMore = useCallback(() => {
    if (isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;
    setIsLoading(true);

    setTimeout(() => {
      setDisplayedCount(prev => Math.min(prev + ITEMS_PER_PAGE, activities.length));
      setIsLoading(false);
      isLoadingRef.current = false;
    }, 300);
  }, [activities.length]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;

      if (isNearBottom && hasMore && !isLoadingRef.current) {
        loadMore();
      }
    },
    [hasMore, loadMore]
  );

  const getActivityIcon = (type: ActivityItem['type']): React.ReactElement => {
    const iconSx = { fontSize: 18, color: 'text.primary', flexShrink: 0 };
    switch (type) {
      case 'donation':
        return <VolunteerActivismIcon sx={iconSx} />;
      case 'purchase':
        return <ShoppingCartIcon sx={iconSx} />;
      case 'lead':
        return <MessageIcon sx={iconSx} />;
      case 'contact':
        return <PeopleIcon sx={iconSx} />;
      default:
        return <MessageIcon sx={iconSx} />;
    }
  };

  const getActivityLabel = (type: ActivityItem['type']) => {
    switch (type) {
      case 'donation':
        return 'תרומה';
      case 'purchase':
        return 'רכישה';
      case 'lead':
        return 'פנייה';
      case 'contact':
        return 'איש קשר';
      default:
        return 'פעילות';
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'donation':
        return alpha(theme.palette.success.main, 0.2);
      case 'purchase':
        return alpha(theme.palette.error.main, 0.2);
      case 'lead':
        return alpha(theme.palette.info.main, 0.2);
      case 'contact':
        return alpha(theme.palette.secondary.main, 0.2);
      default:
        return theme.palette.grey.A200;
    }
  };

  const formatActivityDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const gregorianDate = date.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const time = date.toLocaleTimeString('he-IL', {
        hour: '2-digit',
        minute: '2-digit'
      });

      let hebrewDate = '';
      try {
        const jewishDay = getJewishDay(dayjs(date) as Dayjs);
        if (jewishDay?.jewishDateStrHebrew) {
          hebrewDate = jewishDay.jewishDateStrHebrew;
        }
      } catch (e) {
        // ignore
      }

      return hebrewDate ? `${hebrewDate}, ${gregorianDate}, ${time}` : `${gregorianDate}, ${time}`;
    } catch (e) {
      return dateString;
    }
  };

  const getActivityDescription = (activity: ActivityItem) => {
    if (activity.type === 'donation' && activity.amount) {
      const amount = parseFloat(activity.amount);
      return `תרומה מ${activity.name} בסך ${amount.toLocaleString('he-IL')} ש"ח`;
    }
    if (activity.type === 'purchase' && activity.amount) {
      const amount = parseFloat(activity.amount);
      const productsText = activity.products ? ` - ${activity.products}` : '';
      return `רכישה מ${activity.name} בסך ${amount.toLocaleString('he-IL')} ש"ח${productsText}`;
    }
    if (activity.type === 'lead') {
      return `פנייה חדשה מ${activity.name}${activity.comment ? ` - ${activity.comment}` : ''}`;
    }
    if (activity.type === 'contact') {
      return `נוסף איש קשר חדש: ${activity.name}${activity.comment ? ` - ${activity.comment}` : ''}`;
    }
    return activity.comment || 'פעילות';
  };

  const handleNavigateToActivity = useCallback(
    (activity: ActivityItem) => {
      const basePath = {
        donation: '/desk/donations',
        purchase: '/desk/purchases',
        lead: '/desk/leads',
        contact: '/crm/contacts'
      }[activity.type];
      if (basePath && activity.id) {
        navigate(`${basePath}?id=${String(activity.id)}`);
        onClose();
      }
    },
    [navigate, onClose]
  );
  return (
    <Dialog fullHeight open={open} onClose={onClose} maxWidth="md" fullWidth title="כל הפעילויות" contentPadding="none">
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {shouldFetchData && <ActivitiesFilters onFiltersChange={setFilters} totalResults={activities.length} />}

        <Box
          onScroll={handleScroll}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2
          }}
        >
          {shouldFetchData && fetchingActivities && activities.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 6 }}>
              <Loader size={32} thickness={5} />
            </Box>
          )}
          {displayedActivities.map(activity => (
            <Box
              key={`${activity.type}-${activity.id}`}
              sx={{
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexDirection: 'row-reverse',
                    bgcolor: getActivityColor(activity.type),
                    color: 'text.primary',
                    fontWeight: 600,
                    minWidth: 100,
                    height: 26,
                    borderRadius: '13px',
                    px: 1,
                    fontSize: '0.8125rem',
                    lineHeight: 1
                  }}
                >
                  {getActivityIcon(activity.type)}
                  {getActivityLabel(activity.type)}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexDirection: 'row-reverse' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <IconButton
                    color="primary"
                    shape="circle"
                    size="small"
                    aria-label="מעבר לפעילות"
                    onClick={e => {
                      e.stopPropagation();
                      handleNavigateToActivity(activity);
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: 15, transform: 'rotate(180deg)' }} />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="body" color="text.secondary">
                      {formatActivityDate(activity.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ wordBreak: 'break-word' }}>
                    <Typography variant="body" color="text.primary">
                      {getActivityDescription(activity)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}

          {hasMore && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 3,
                minHeight: 60
              }}
            >
              {isLoading && <Loader size={24} thickness={5} />}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default AllActivitiesDialog;
